import { ConfigProvider, Form, Input, Radio } from 'antd';
import { TYPE_OF_EMPLOYEE_OPTIONS } from '@/shared/employee';
import { formValidateAndSetNumber } from '@/utils/inputApi';
import AdditionalHolidayDaysSection from '../../AdditionalHolidayDaysSection';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';

/** Annual leave tracker type and the holiday day counts. */
export default function EmployeeHolidayDaysFields() {
  const {
    calculationData,
    employeeId,
    employeeTrackerTypeKeys,
    employeeTrackerTypeState,
    endDate,
    form,
    isEdited,
    isEmployee,
    isExistingUser,
    isProfile,
    isSelectedEmployment,
    numberOfAdditionalHolidayDays,
    numberOfHolidayDaysContractKeys,
    numberOfHolidayDaysContractState,
    numberOfHolidayDaysPast,
    numberOfHolidayDaysPastKeys,
    numberOfHolidayDaysPastState,
    numberOfHolidayDaysUsed,
    numberOfHolidayDaysUsedKeys,
    numberOfHolidayDaysUsedState,
    readOnly,
    setShowAdditionalDays,
    showAdditionalDays,
    startDate,
    t,
  } = useEmployeeGeneralDataForm();

  // On the profile: laid out by EmployeeHolidayProfilePanel.
  if (isProfile) return null;

  return (
    <>
      {!isEdited && employeeTrackerTypeState?.visible && (
        <>
          <Form.Item
            name={employeeTrackerTypeKeys}
            label={t('typeOfEmployee', { ns: 'forms' })}
          >
            <Radio.Group
              className="grid gap-4 sm:flex [&_label]:items-start [&_label]:justify-start"
              disabled={employeeTrackerTypeState?.disabled}
            >
              {TYPE_OF_EMPLOYEE_OPTIONS.map(({ key, value }) => (
                <Radio
                  key={key}
                  value={value}
                  className="mr-0 w-full rounded border border-zinc-200 bg-white px-2 py-1 hover:border-accent"
                >
                  {t(key, { ns: 'forms' })}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
          <hr className="my-5" />
        </>
      )}

      {isExistingUser && (
        <>
          <div className="mb-1 mr-2 h-8 border border-transparent font-bold [&_label]:w-full">
            {t('holidayDaysYear', { ns: 'messages' })}:{' '}
            {calculationData?.calculation?.holidayDaysYear}
          </div>

          {numberOfHolidayDaysPastState?.visible && (
            <Form.Item
              name={numberOfHolidayDaysPastKeys}
              label={t('numberOfHolidayDaysPast', { ns: 'messages' })}
              layout="horizontal"
              defaultValue={0}
              labelCol={{
                span: 12,
                className:
                  'mr-2 border border-transparent h-8 [&_label]:w-full',
              }}
            >
              <Input
                min={0}
                type="number"
                disabled={
                  !isSelectedEmployment ||
                  numberOfHolidayDaysUsed ||
                  numberOfHolidayDaysPastState?.disabled
                }
                placeholder={t('numberOfHolidayDaysPast', { ns: 'messages' })}
                onChange={(e) =>
                  formValidateAndSetNumber(form, e, numberOfHolidayDaysPastKeys)
                }
              />
            </Form.Item>
          )}

          {numberOfHolidayDaysUsedState?.visible && (
            <Form.Item
              name={numberOfHolidayDaysUsedKeys}
              label={t(
                endDate?.length > 1
                  ? t('numberOfHolidayDaysUsedContract', { ns: 'messages' })
                  : t('numberOfHolidayDaysUsedYear', { ns: 'messages' })
              )}
              layout="horizontal"
              defaultValue={0}
              labelCol={{
                span: 12,
                className:
                  'mr-2 border border-transparent h-8 [&_label]:w-full',
              }}
            >
              <Input
                min={0}
                type="number"
                disabled={
                  !isSelectedEmployment ||
                  numberOfHolidayDaysPast ||
                  numberOfHolidayDaysUsedState?.disabled
                }
                placeholder={
                  endDate?.length > 1
                    ? t('numberOfHolidayDaysUsedContract', { ns: 'messages' })
                    : t('numberOfHolidayDaysUsedYear', { ns: 'messages' })
                }
                onChange={(e) =>
                  formValidateAndSetNumber(form, e, numberOfHolidayDaysUsedKeys)
                }
              />
            </Form.Item>
          )}
        </>
      )}

      {numberOfHolidayDaysContractState?.visible && (
        <Form.Item
          name={numberOfHolidayDaysContractKeys}
          label={t('numberOfHolidayDaysContract', { ns: 'messages' })}
          layout="horizontal"
          defaultValue={0}
          labelCol={{
            span: 12,
            className: 'mr-2 border border-transparent h-8 [&_label]:w-full',
          }}
        >
          <Input
            min={0}
            type="number"
            disabled={
              readOnly ||
              (!startDate &&
                !endDate &&
                numberOfHolidayDaysContractState?.disabled)
            }
            placeholder={t('numberOfHolidayDaysContract', { ns: 'messages' })}
            onChange={(e) =>
              formValidateAndSetNumber(form, e, numberOfHolidayDaysContractKeys)
            }
          />
        </Form.Item>
      )}

      {isEdited &&
      employeeId &&
      (numberOfAdditionalHolidayDays > 0 || showAdditionalDays) ? (
        // Saves on its own, so it stays usable in the read-only profile view.
        <ConfigProvider componentDisabled={false}>
          <AdditionalHolidayDaysSection
            employeeId={employeeId}
            totalDaysFromServer={numberOfAdditionalHolidayDays}
          />
        </ConfigProvider>
      ) : isEdited && employeeId && !isEmployee ? (
        <button
          type="button"
          className="mt-1 text-xs text-blue-500 hover:text-blue-700 hover:underline"
          onClick={() => setShowAdditionalDays(true)}
        >
          + {t('addAdditionalDays', { ns: 'messages' })}
        </button>
      ) : null}
    </>
  );
}
